import { createTheme } from '@material-ui/core/styles'
import variables from './_variables.scss'
import { isRtl } from '../i18nInit'
import type {} from '@material-ui/lab/themeAugmentation'
import type {} from './themeAugmentation'
import { tokens } from './tokens'

export const primaryBlue = tokens.brand.blue
export const primaryBlue2 = tokens.brand.blue
export const primaryLightBlue = tokens.brand.blueTint

export const darkBlue = tokens.text.primary
export const lightBlue = '#82D1F8'
export const lighterBlue = '#E5F1FF'
export const lightestBlue = '#e7f0fe'
export const altBlue = tokens.text.secondary

export const primaryText = darkBlue
export const secondaryText = altBlue

export const darkGreen = '#318713'
export const darkerGreen = '#1A6300'
export const altGreen = '#39AF10'

export const successGreen = '#66B774'

export const lightGreen = '#96DDA3'
export const lighterGreen = '#A1E8AE'

export const notificationLightGreen = '#E7FFEB'
export const notificationLightBlue = '#E9F0FD'
export const notificationLightRed = '#FFE7EA'

//export const darkGreen = '#318713'
export const darkSuccess = '#067c00'

export const veryLightBlue = '#F2F8FF'
export const red = '#D7263D'
export const listLightBlue = '#F5F9FF'
export const gray = '#F5F5F9'
export const darkGray = '#CCCCCC'
// Was #9f9f9f (2.64:1 on white). #707173 hits 4.56:1 for WCAG 2.2 SC 1.4.3.
export const darkerGray = '#707173'
export const borderGray = '#edeff6'
export const accessibleGray = '#8E92A9'
export const accessibleDarkerGray = '#495881'
export const accessibleLighterGray = '#6F768B'

const btnPrimaryBgHover = tokens.brand.blueHover
const btnPrimaryBgActive = tokens.brand.bluePressed
const btnSecondaryBgHover = lighterBlue
const btnSecondaryBgActive = '#CBE2FF'

export const challengeGoalYellow = '#FFE1A6'
export const challengeGoalGreen = '#8FD9B6'
export const challengeGoalGray = '#D0D6E3'

export const whiteForDarkBackground = '#E6EBF5'

export const bgGray = '#F2F2F6'

export const Theme = createTheme({
  direction: isRtl ? 'rtl' : 'ltr',
  breakpoints: {
    values: {
      xs: 0,
      sm: 768,
      md: 992,
      lg: 1280,
      xl: 1920,
    },
  },
  palette: {
    primary: {
      main: tokens.brand.blue,
      light: tokens.brand.blueHover,
      dark: tokens.brand.bluePressed,
    },
    secondary: {
      main: tokens.brand.blue,
    },
    text: {
      primary: tokens.text.primary,
      secondary: tokens.text.secondary,
    },
    error: {
      main: red,
    },
    grey: { 500: gray },
    border: {
      default: tokens.border.default,
      subtle: tokens.border.subtle,
      focus: tokens.border.focus,
    },
  },
  typography: {
    fontFamily: `${variables.font_family}`,
    body1: {
      fontSize: '1rem',
      lineHeight: '1.25rem',
      fontWeight: 'inherit',

      'html[dir=rtl] &': {
        textAlign: 'right',
      },
    },
    body2: {
      fontSize: '1rem',
      lineHeight: '1.125rem',
      fontWeight: 400,
    },
    h1: {
      fontSize: '2.25rem',
      lineHeight: '2.625rem',
      fontWeight: 'normal',
      // @ts-ignore
      marginBottom: '1.5625rem',
      // @ts-ignore
      whiteSpace: 'pre-line',
    },
    h2: {
      fontSize: '1.125rem',
      lineHeight: '1.125rem',
      fontWeight: 'bold',
      // @ts-ignore
      marginBottom: '1rem',
    },
    h3: {
      fontSize: '0.9375rem',
      lineHeight: '0.9375rem',
      fontWeight: 500,
    },

    subtitle1: {
      fontSize: '1.25rem',
      lineHeight: '1.625rem',
      // @ts-ignore
      marginBottom: '1.5625rem',
      /*'@media (max-width: 320px)': {
        fontSize: 16,
        lineHeight: '20px',
      }*/
    },
    subtitle2: {
      fontWeight: 'bold',
      //fontSize: '16px',
      fontSize: '1.25rem',
      lineHeight: '1.25rem',
      textAlign: 'left',
      // @ts-ignore
      marginBottom: '0.625rem',
      marginTop: '0.125rem',

      /*'@media (min-width: 992px)': {
        fontSize: '20px',
      },*/

      'html[dir=rtl] &': {
        textAlign: 'right',
      },
    },
    caption: {
      fontWeight: 'bold',
      fontSize: '0.9375rem',
      lineHeight: '1.125rem',
      textAlign: 'left',
      // @ts-ignore
      marginBottom: '0.625rem',
      '@media (min-width: 992px)': {
        fontSize: '1.1875rem',
        lineHeight: '1.4375rem',
      },
    },
  },
  overrides: {
    MuiFormControl: {
      marginNormal: {
        marginTop: 0,
        marginBottom: 16,
      },
      marginDense: {
        marginTop: 0,
        marginBottom: 12,
      },
    },
    MuiOutlinedInput: {
      notchedOutline: {
        // Resting border must stay >=3:1 against the page (WCAG 2.2 SC 1.4.11).
        // gray (#F5F5F9) matched the fill and was invisible (~1.05:1); border.default
        // reaches 3.23:1 on white.
        borderColor: tokens.border.default,
      },
      root: {
        //color: darkGray,
        backgroundColor: '#ffffff',
        borderRadius: 10,
        '&:hover, &$focused': {
          backgroundColor: veryLightBlue,
        },
        '&:hover $notchedOutline': {
          borderColor: primaryBlue, //theme.palette.primary.main,
        },
        '&$focused $notchedOutline': {
          borderWidth: 1,
        },
        '&$error': {
          color: red,
        },
        '&$error$focused': {
          color: `${variables.primary_text_color}`,
        },
        '&$error$focused $notchedOutline': {
          borderColor: primaryBlue,
        },
        '&.Mui-disabled': {
          backgroundColor: gray,
          cursor: 'default',
        },
        // Prevent hover styles on disabled inputs
        '&.Mui-disabled:hover': {
          backgroundColor: gray,
        },
        '&.Mui-disabled $notchedOutline': {
          borderColor: tokens.border.default,
        },
        '&.Mui-disabled:hover $notchedOutline': {
          borderColor: tokens.border.default,
        },
      },
      input: {
        fontSize: '1rem',
        paddingTop: '14.5px',
        paddingBottom: '14.5px',
        lineHeight: 1.5,
        '&::placeholder': {
          // Placeholder is real text → needs >=4.5:1 (WCAG 2.2 SC 1.4.3). border.default
          // (#8A8EA5) is only ~3.2:1 on white; text.secondary reaches 7:1.
          color: tokens.text.secondary,
          opacity: 1,
        },
        '&:-webkit-autofill': {
          WebkitBoxShadow: '0 0 0 1000px #ffffff inset',
        },
        'div:not(.Mui-disabled):hover > &:-webkit-autofill, &:focus:-webkit-autofill': {
          WebkitBoxShadow: '0 0 0 1000px #F2F8FF inset',
        },
        '&.Mui-disabled': {
          cursor: 'default',
          color: darkBlue,
          '-webkit-text-fill-color': darkBlue,
          opacity: 1,
          paddingTop: '13px',
          paddingBottom: '13px',
          lineHeight: '1.375rem',
          height: '1.375rem',
        },
        'textarea&.Mui-disabled': {
          paddingTop: 2,
          paddingBottom: 3,
          //lineHeight: '18px',
          //height: '18px',
        },
      },
      adornedStart: {
        'html[dir=rtl] &': {
          //paddingLeft: 0,
          paddingRight: '14px',
        },
      },
      adornedEnd: {
        'html[dir=rtl] &': {
          //paddingRight: 0,
          paddingLeft: '14px',
        },
      },
      inputAdornedStart: {
        'html[dir=rtl] &': {
          paddingRight: 0,
        },
      },
    },
    MuiInputLabel: {
      filled: {
        fontSize: '1rem',
        lineHeight: '1.125rem',
        transform: 'translate(10px, 15px) scale(1) !important',

        'html[dir=rtl] &': {
          transformOrigin: '100% 0',
          transform: 'translate(-10px, 15px) scale(1) !important',
        },

        '&$shrink': {
          transform: 'translate(10px, 6px) scale(0.65) !important',

          'html[dir=rtl] &': {
            transformOrigin: '100% 0',
            transform: 'translate(-10px, 6px) scale(0.65) !important',
          },
        },
      },
      formControl: {
        'html[dir=rtl] &': {
          left: 'auto',
          right: 0,
        },
      },
    },
    /*MuiInput: {
      underline: {
        fontSize: '16px',
      },
    },*/
    MuiAutocomplete: {
      input: {
        marginRight: isRtl ? 'unset' : '25px',
        marginLeft: !isRtl ? 'unset' : '25px',
      },
      endAdornment: {
        right: isRtl ? 'unset' : '7px',
        left: !isRtl ? 'unset' : '7px',
      },
    },
    MuiFilledInput: {
      root: {
        backgroundColor: '#ffffff',
        borderTopLeftRadius: 10,
        borderTopRightRadius: 10,
        borderBottomRightRadius: 10,
        borderBottomLeftRadius: 10,
        // Resting border must be visible at >=3:1 against the white page (WCAG 2.2
        // SC 1.4.11). transparent on the grey fill was invisible (~1.05:1);
        // border.default (#8A8EA5) reaches 3.23:1. Covers filled inputs & comboboxes.
        border: `1px solid ${tokens.border.default}`,
        '&:hover': {
          borderColor: primaryBlue,
          backgroundColor: veryLightBlue,
          '@media (hover: none)': {
            borderColor: tokens.border.default,
            backgroundColor: '#ffffff',
          },
        },
        '&$focused': {
          borderColor: primaryBlue,
          backgroundColor: veryLightBlue,
          '@media (hover: none)': {
            borderColor: `${primaryBlue}!important`,
            backgroundColor: `${veryLightBlue}!important`,
          },
        },
        '&.Mui-disabled': {
          borderColor: tokens.border.default,
          backgroundColor: gray,
          cursor: 'default',
        },
        '&$error': {
          borderColor: red,
        },
      },
      input: {
        fontSize: '1rem',
        lineHeight: '1.375rem',
        height: 'auto',
        paddingTop: '22px',
        paddingBottom: '5px',
        paddingLeft: '10px',
        paddingRight: '10px',
        borderTopLeftRadius: 10,
        borderTopRightRadius: 10,
        borderBottomRightRadius: 10,
        borderBottomLeftRadius: 10,
        '&::placeholder': {
          // Placeholder is real text → needs >=4.5:1 (WCAG 2.2 SC 1.4.3). border.default
          // (#8A8EA5) is only ~3.2:1 on white; text.secondary reaches 7:1.
          color: tokens.text.secondary,
          opacity: 1,
        },
        '&:-webkit-autofill': {
          WebkitBoxShadow: '0 0 0 1000px #ffffff inset',
        },
        'div:not(.Mui-disabled):hover > &:-webkit-autofill, &:focus:-webkit-autofill': {
          WebkitBoxShadow: '0 0 0 1000px #F2F8FF inset',
        },
        '&.Mui-disabled': {
          cursor: 'default',
          color: darkBlue,
          '-webkit-text-fill-color': darkBlue,
          opacity: 1,
        },
        '&[type=date], &[type=datetime-local]': {
          height: '1.375rem',
          //marginTop: '-2px',
          marginLeft: '-44px',
          //paddingLeft: '42px',
          paddingLeft: '44px !important',
          WebkitAppearance: 'none',

          'html[dir=rtl] &': {
            marginLeft: 0,
            paddingLeft: 0,
            marginRight: '-44px',
            paddingRight: '44px !important',
            textAlign: 'right',
          },

          '&::-webkit-date-and-time-value': {
            textAlign: 'left',
            'html[dir=rtl] &': {
              textAlign: 'right',
            },
          },
        },
      },
      multiline: {
        paddingLeft: '10px',
        paddingRight: '10px',
        alignItems: 'flex-start',
        paddingTop: '19px !important',
        paddingBottom: '5px !important',
      },
      inputMultiline: {
        borderRadius: 0,
        lineHeight: '1.375rem',
      },
      inputMarginDense: {
        paddingTop: '19px',
        paddingBottom: '5px',
      },
      adornedStart: {
        paddingLeft: '10px',

        'html[dir=rtl] &': {
          paddingLeft: 0,
          paddingRight: '10px',
        },

        /*'.date-range &': {
          padding: '0 !important',
        },*/
      },
      adornedEnd: {
        'html[dir=rtl] &': {
          paddingLeft: 12,
        },
      },
      inputAdornedStart: {
        // TODO: PROBLEMS WITH SELECT WITH ICON
        //paddingLeft: '0 !important',

        'html[dir=rtl] &': {
          paddingLeft: '10px !important',
          //paddingRight: '0 !important',
        },
      },
    },
    MuiInputBase: {
      root: {
        verticalAlign: 'bottom',
      },
      // MUI v4 bakes `outline: 0` into `.MuiInputBase-input`, which knocks out
      // the global `:focus-visible` ring on native <input>/<select> elements.
      // Re-apply it here so keyboard focus is visible — but only when the user
      // is actually navigating by keyboard. The spec matches `:focus-visible`
      // on inputs even when clicked, which would show the ring on every mouse
      // focus; the `html[data-modality="keyboard"]` gate (set by
      // installModalityTracker) suppresses that.
      input: {
        'html[data-modality="keyboard"] &:focus-visible': {
          outline: `2px solid ${tokens.brand.bluePressed}`,
          outlineOffset: 3,
        },
      },
    },
    // MUI v4's ButtonBase bakes `outline: 0` into its root, overriding the
    // global `:focus-visible` rule on every Button / IconButton / ListItem
    // (button) / Tab in the app. Restore the focus ring at the same JSS layer
    // so source order favors our rule.
    MuiButtonBase: {
      root: {
        '&:focus-visible': {
          outline: `2px solid ${tokens.brand.bluePressed}`,
          outlineOffset: 2,
        },
        // Radio / Checkbox / Switch put focus on a hidden inner <input>, so the
        // wrapper never matches CSS :focus-visible. MUI instead adds the
        // .Mui-focusVisible class to the wrapper on keyboard focus — mirror the
        // ring onto it so those controls show a visible focus indicator (SC 2.4.7).
        '&.Mui-focusVisible': {
          outline: `2px solid ${tokens.brand.bluePressed}`,
          outlineOffset: 2,
        },
      },
    },
    // MuiTabs-scroller uses `overflow: hidden` to enable horizontal scrolling,
    // which clips the outset focus ring. Use a negative outline-offset so the
    // ring is inset within the tab box and stays visible.
    MuiTab: {
      root: {
        '&:focus-visible': {
          outlineOffset: -2,
        },
      },
    },
    MuiNativeSelect: {
      icon: {
        top: 'auto',
      },
    },
    MuiSelect: {
      icon: {
        top: 'auto',
      },
      filled: {
        paddingBottom: '5px',
        paddingRight: '19px',
        borderRadius: '10px !important',

        'html[dir=rtl] &': {
          paddingRight: '10px',
          paddingLeft: '19px !important',
        },

        '&:hover': {
          '@media (hover: none)': {
            backgroundColor: 'transparent !important',
          },
        },
      },
      /*iconFilled: {
        padding: '19px 32px 5px 0 !important',
      },*/
      select: {
        display: 'inline-block',
        margin: 0,
        //borderRadius: '25px !important',
        //padding: '0 24px 0 10px !important',
        padding: '0 24px 0 10px',
        //lineHeight: '24px',
        //height: '24px',
        lineHeight: '1.5rem',
        height: '1.5rem',
        fontSize: '0.625rem',
        fontWeight: 700,
        width: '100%',
        //textTransform: 'uppercase',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        borderWidth: '1px',
        borderStyle: 'solid',

        'html[dir=rtl] &': {
          padding: '0 10px 0 24px',
        },

        borderColor: primaryBlue,
        backgroundColor: primaryBlue,
        color: 'white',

        '&:hover': {
          borderColor: btnPrimaryBgHover,
          backgroundColor: btnPrimaryBgHover,
          '@media (hover: none)': {
            borderColor: `${primaryBlue} !important`,
            backgroundColor: `${primaryBlue} !important`,
          },
          '&.MuiTablePagination-select': {
            backgroundColor: 'rgba(0, 0, 0, 0.05)',
          },
        },
        '&:active': {
          borderColor: btnPrimaryBgActive,
          backgroundColor: btnPrimaryBgActive,
          '@media (hover: none)': {
            borderColor: `${btnPrimaryBgActive} !important`,
            backgroundColor: `${btnPrimaryBgActive} !important`,
          },
          '&.MuiTablePagination-select': {
            backgroundColor: 'rgba(0, 0, 0, 0.05)',
          },
        },
        '&:focus': {
          borderRadius: '25px',
          backgroundColor: primaryBlue,
        },

        '&.MuiSelect-outlined': {
          borderColor: primaryBlue,
          backgroundColor: 'white',
          color: primaryBlue,

          '&:hover': {
            borderColor: primaryBlue,
            backgroundColor: btnSecondaryBgHover,
            '@media (hover: none)': {
              backgroundColor: `white !important`,
            },
          },
          '&:active': {
            borderColor: primaryBlue,
            backgroundColor: btnSecondaryBgActive,
            '@media (hover: none)': {
              backgroundColor: `${btnSecondaryBgActive} !important`,
            },
          },
        },
      },
      iconFilled: {
        'html[dir=rtl] &': {
          left: '7px',
          right: 'auto',
        },
      },
    },
    MuiInputAdornment: {
      root: {
        fontSize: '1rem',

        /*'.date-range & svg': {
          marginLeft: '10px',

          /!*'html[dir=rtl] &': {
            paddingRight: '10px',
            paddingLeft: 0,
          },*!/
        },*/
      },
      positionStart: {
        color: darkBlue,
        // Focus the input when the icon is clicked
        pointerEvents: 'none',

        'html[dir=rtl] &': {
          marginLeft: '8px',
          marginRight: 0,
        },

        '.Mui-focused > &': {
          color: primaryBlue,
        },
      },
      positionEnd: {
        'html[dir=rtl] &': {
          marginLeft: 0,
          marginRight: '8px',
        },
      },
    },
    MuiIconButton: {
      root: {
        paddingTop: '10px',
        paddingBottom: '10px',
      },
      edgeEnd: {
        // Edge-end adornment icons (edit pen, etc.) must reach >=3:1 (WCAG 2.2 SC
        // 1.4.11). darkGray (#CCCCCC) was 1.6:1 on white; darkerGray is 4.9:1.
        color: darkerGray,
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.03)',
          '@media (hover: none)': {
            backgroundColor: 'transparent',
          },
        },
        '&:active': {
          '@media (hover: none)': {
            backgroundColor: 'rgba(0, 0, 0, 0.03)',
          },
        },
        /* TODO: multiline stuff */
        'textarea + div &': {
          marginTop: '7px',
          marginRight: '-10px',
        },

        'html[dir=rtl] &': {
          marginLeft: '-12px',
          marginRight: 'auto',
        },
      },
    },
    MuiButton: {
      root: {
        display: 'block',
        textAlign: 'center',
        border: 0,
        textTransform: 'none',
        borderRadius: '8px',

        fontWeight: 500,
        padding: '14px 16px',
        fontSize: '1rem',
        height: 'auto',
        lineHeight: '1.25rem',
        whiteSpace: 'nowrap',
        margin: '0 auto',

        '& svg': {
          verticalAlign: 'middle',
        },

        '&.Mui-disabled': {
          borderColor: `${darkerGray} !important`,
          color: `${darkerGray} !important`,
        },
      },
      outlined: {
        backgroundColor: '#ffffff',
        padding: '13px 16px',
      },
      text: {
        background: 'white',
        color: primaryBlue,
        minWidth: '36px',
        padding: '6px 10px',
      },
      containedPrimary: {
        backgroundColor: primaryBlue,
        '&:hover': {
          backgroundColor: btnPrimaryBgHover,
          '@media (hover: none)': {
            backgroundColor: primaryBlue,
          },
        },
        '&:active': {
          backgroundColor: btnPrimaryBgActive,
          '@media (hover: none)': {
            backgroundColor: `${btnPrimaryBgActive} !important`,
          },
        },
      },
      outlinedPrimary: {
        // Text uses blueHover in the resting state too, so every state keeps a
        // >=4.5:1 contrast ratio (WCAG 2.2 SC 1.4.3): 5.6:1 on white vs 4.6:1
        // for primaryBlue, and 4.88:1 on the light-blue hover tint where
        // primaryBlue only reaches 4.06:1.
        color: tokens.brand.blueHover,
        borderColor: primaryBlue,
        '&:hover': {
          color: tokens.brand.blueHover,
          borderColor: tokens.brand.blueHover,
          backgroundColor: btnSecondaryBgHover,
          '@media (hover: none)': {
            backgroundColor: '#ffffff',
          },
        },
        '&:active': {
          // The darker active tint needs an even darker text colour to stay above
          // 4.5:1; bluePressed reaches 5.90:1.
          color: tokens.brand.bluePressed,
          borderColor: tokens.brand.bluePressed,
          backgroundColor: btnSecondaryBgActive,
          '@media (hover: none)': {
            backgroundColor: `${btnSecondaryBgActive} !important`,
          },
        },
      },
      sizeSmall: {
        //display: 'inline-block',
        display: 'inline-flex',
        margin: 0,
        minWidth: 'auto',
        width: 'auto',
        //padding: '0 8px',
        padding: '0 0.5rem',
        //lineHeight: '32px',
        //height: '32px',
        height: '2rem',
        fontSize: '0.875rem',
        fontWeight: 600,
        whiteSpace: 'nowrap',
        borderRadius: '8px',
        /*'& svg': {
          marginTop: '-2px',
        },*/
      },
      sizeLarge: {
        fontSize: '1.125rem',
        margin: '0 auto 16px',
        padding: '6px 24px',
      },
      containedSizeSmall: {
        //lineHeight: '32px',
        lineHeight: '2rem',
      },
      outlinedSizeSmall: {
        //lineHeight: '30px',
        lineHeight: '1.875',
      },
      textSecondary: {
        '&:hover': {
          backgroundColor: 'rgba(255,255,255,.5)',
          '@media (hover: none)': {
            backgroundColor: '#ffffff',
          },
        },
        '&:active, &:focus': {
          backgroundColor: 'rgba(255,255,255,.5)',
          '@media (hover: none)': {
            backgroundColor: `rgba(255,255,255,.5) !important`,
          },
        },
      },
      containedSecondary: {
        transition:
          'background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, border 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, opacity 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
        backgroundColor: `${darkGreen} !important`,
        //backgroundColor: success,
        '&:hover': {
          opacity: 0.9,
          '@media (hover: none)': {
            opacity: 1,
          },
        },
        '&:active': {
          opacity: 0.7,
          '@media (hover: none)': {
            opacity: 1,
          },
        },
      },
      label: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        /*'& svg': {
          verticalAlign: 'bottom',
        },*/
      },
    },
    MuiLink: {
      underlineAlways: {
        //fontSize: '12px',
        lineHeight: '18px',
        textDecoration: 'none',
        borderBottom: `1px solid ${primaryBlue}`,
      },
      underlineHover: {
        '&:hover': {
          textDecoration: 'none',
          borderBottom: `1px solid ${primaryBlue}`,
        },
      },
    },
    MuiBackdrop: {
      root: {
        backgroundColor: 'rgba(36, 40, 78, 0.2)',
        backdropFilter: 'blur(5px)',
      },
    },
    MuiDrawer: {
      paper: {
        backgroundColor: '#ffffff',
        borderTopLeftRadius: '30px',
        borderTopRightRadius: '30px',
        //padding: '22px 15px 34px 15px',
        padding: '22px 15px 0 15px',
        width: '100%',
        maxWidth: 600,
        margin: '0 auto',

        /* iOS Safari 11.2, Safari 11 */
        '@supports (bottom: constant(safe-area-inset-bottom))': {
          paddingBottom: 'constant(safe-area-inset-bottom)',
        },

        /* iOS Safari 11.4+, Safari 11.1+, Chrome 69+, Opera 56+ */
        '@supports (bottom: env(safe-area-inset-bottom))': {
          paddingBottom: 'env(safe-area-inset-bottom)',
        },
      },
      paperAnchorBottom: {
        maxHeight: '80vh',
      },
    },
    MuiList: {
      padding: {
        paddingTop: 0,
        paddingBottom: 0,
      },
    },
    MuiListItem: {
      root: {
        borderRadius: 10,
        marginBottom: 12,
        paddingTop: 9,
        paddingBottom: 9,
        border: '1px solid transparent',
        backgroundColor: 'white',
        textAlign: 'left',

        'html[dir=rtl] &': {
          textAlign: 'right',
        },
      },
      button: {
        backgroundColor: gray,
        '&:hover': {
          backgroundColor: gray,
          '@media (hover: none)': {
            backgroundColor: gray,
          },
          '@media (hover: hover)': {
            borderColor: primaryBlue,
            backgroundColor: listLightBlue,
          },
        },
        '&$selected, &$selected:hover': {
          backgroundColor: listLightBlue,
          borderColor: primaryBlue,
        },
      },
      gutters: {
        paddingLeft: 10,
        paddingRight: 10,
      },
    },
    MuiListItemIcon: {
      root: {
        minWidth: 24,
        color: darkBlue,
      },
    },
    MuiListItemText: {
      root: {
        marginLeft: '10px',
        marginRight: '10px',
      },
      primary: {
        fontSize: '1rem',
      },
      secondary: {
        //fontSize: '14px',
      },
    },
    /*PrivateSwitchBase: {
      root: {
        padding: '10px',
      },
    },*/
    MuiTooltip: {
      tooltip: {
        backgroundColor: red,
        color: '#ffffff',
      },
      tooltipPlacementTop: {
        top: 15,
        //'@media (min-width: 768px)': {
        '@media (min-width: 375px)': {
          top: 5,
        },
      },
      arrow: {
        color: red,
      },
    },
    MuiChip: {
      root: {
        marginRight: '10px',
        marginBottom: '10px',
        borderColor: listLightBlue,
        backgroundColor: listLightBlue,
        borderWidth: '1px',
        borderStyle: 'solid',
      },
      clickable: {
        '&:active': {
          boxShadow: 'none',
        },
        '&:hover': {
          backgroundColor: listLightBlue,
          borderColor: primaryBlue,
          // Reset on touch devices, it doesn't add specificity
          '@media (hover: none)': {
            borderColor: listLightBlue,
          },
        },
        '&:focus': {
          backgroundColor: listLightBlue,
        },
      },
      colorPrimary: {
        color: 'white',
        borderColor: primaryBlue,
      },
      colorSecondary: {
        color: 'white',
        backgroundColor: red,
      },
      outlined: {
        backgroundColor: listLightBlue,
        borderColor: `${primaryBlue} !important`,
        //color: primaryBlue,
      },
      sizeSmall: {
        height: '1.5rem',
        borderRadius: '1rem',
      },
    },
    // Radio / Checkbox / Switch all render a PrivateSwitchBase button whose
    // focus ring, at the global +2px outset offset, floats away from the small
    // control. Inset it to -4px so the ring hugs the control (WCAG 2.2 SC 2.4.7).
    // !important beats the equal-specificity global .Mui-focusVisible rule.
    MuiRadio: {
      root: {
        '&.Mui-focusVisible': {
          outlineOffset: '-4px !important',
        },
      },
    },
    MuiCheckbox: {
      root: {
        '&.Mui-focusVisible': {
          outlineOffset: '-4px !important',
        },
      },
    },
    MuiSwitch: {
      switchBase: {
        '&.Mui-focusVisible': {
          outlineOffset: '-4px !important',
        },
      },
      /*root: {
        '& $checked': {
          '& + $track': {
            backgroundColor: `${variables.success_color}`,
          },
          color: `${variables.success_color}`,
        },
        switchBase: {
          '& + $track': {
            backgroundColor: `${variables.disabled_color}`,
          },
          color: `${variables.disabled_color}`,
        },
      },*/
      colorPrimary: {
        //color: `#F5F5F9 !important`,
        /*'&$checked': {
          color: `${red} !important`,
        },*/
        '&$checked + $track': {
          backgroundColor: `${darkGreen} !important`,
        },
      },
      colorSecondary: {
        /*color: `#F5F5F9 !important`,
        '&$checked': {
          color: `${red} !important`,
        },*/
        '&$checked + $track': {
          backgroundColor: `${red} !important`,
        },
      },
      track: {
        opacity: 0.2,
      },
    },
    MuiMenuItem: {
      root: {
        marginBottom: 0,
        fontSize: '0.875rem',
        lineHeight: '1.5rem',
        padding: '4px 16px',
        fontWeight: 400,
        minHeight: 'auto',
        border: 'none',
        borderRadius: 0,
        borderTop: `1px solid ${borderGray} !important`,
        backgroundColor: 'transparent !important',
        '&$selected, &&:hover': {
          color: primaryBlue,
          fontWeight: 400,
        },
        // Menu items span the full width of the Menu Paper, which uses `overflow`
        // (auto/hidden) for scrolling and clips the outset focus ring on every side.
        // Inset the ring with a negative outline-offset so it stays fully visible
        // (SC 2.4.7) — same approach as MuiTab inside the scrollable Tabs container.
        '&:focus-visible, &.Mui-focusVisible': {
          outlineOffset: -2,
        },
      },
    },
    MuiFormControlLabel: {
      label: {
        fontSize: '1rem',
      },
      root: {
        marginLeft: '-8px',
        'html[dir=rtl] &': {
          marginLeft: '16px',
          marginRight: '-8px',
        },
      },
    },
    MuiAccordion: {
      root: {
        backgroundColor: gray,
        marginBottom: '12px !important',
        border: `1px solid ${gray}`,
        '&$expanded, &:hover': {
          border: `1px solid ${primaryBlue}`,
          marginTop: 0,
        },
        '&:before': {
          content: 'none',
        },
      },
      rounded: {
        borderRadius: '10px',
        '&:first-child': {
          borderTopLeftRadius: '10px',
          borderTopRightRadius: '10px',
        },
        '&:last-child': {
          borderBottomLeftRadius: '10px',
          borderBottomRightRadius: '10px',
        },
      },
    },
    MuiAccordionSummary: {
      root: {
        padding: '0 10px',
        minHeight: '46px',
        '&$expanded': {
          minHeight: '46px',
        },
        '&$focused': {
          borderRadius: '10px',
        },
      },
      content: {
        alignItems: 'center',
        margin: '11px 0',
        '&$expanded': {
          margin: 0,
        },
      },
    },
    MuiAccordionDetails: {
      root: {
        padding: '12px 10px 12px 10px',
      },
    },
    MuiStepper: {
      vertical: {
        padding: 0,
      },
    },
    MuiStepContent: {
      root: {
        'html[dir=rtl] &': {
          borderLeft: 'none',
          borderRight: '1px solid #bdbdbd',
          marginLeft: 0,
          marginRight: 12,
          paddingLeft: 8,
          paddingRight: 20,
        },
      },
    },
    MuiStepConnector: {
      vertical: {
        //padding: 0,
        'html[dir=rtl] &': {
          marginLeft: 0,
          marginRight: 12,
        },
      },
      lineVertical: {
        //minHeight: '12px',
        minHeight: 5,
        /*'&$completed': {
          minHeight: 0,
        },*/

        'html[dir=rtl] &': {
          borderLeftWidth: 0,
          borderRightStyle: 'solid',
          borderRightWidth: '1px',
        },
      },
      /*completed: {
        '& .MuiStepConnector-line': {
          minHeight: 0,
        },
      },*/
    },
    MuiStepIcon: {
      root: {
        // Inactive step circle carries a white number; MUI's default disabled grey
        // gave ~2.6:1, below WCAG 2.2 SC 1.4.3 (4.5:1). darkerGray → white 4.9:1.
        // Active/completed keep primary blue via MUI's higher-specificity rules.
        color: darkerGray,
      },
      text: {
        fontSize: '0.75rem',
      },
    },
    MuiStepLabel: {
      root: {
        marginBottom: '8px',
      },
      active: {
        color: `${primaryBlue} !important`,
      },
      iconContainer: {
        paddingRight: '8px',

        'html[dir=rtl] &': {
          paddingRight: '0',
          paddingLeft: '8px',
        },
      },
    },
    MuiFormHelperText: {
      root: {
        fontSize: '0.75rem !important',
        lineHeight: '1.25rem !important',
        fontWeight: 400,

        'html[dir=rtl] &': {
          textAlign: 'right',
        },
      },
      contained: {
        marginLeft: 10,
        marginRight: 10,
      },
    },
  },
  props: {
    MuiButtonBase: {
      disableRipple: true,
    },
  },
})
